import { useEffect, useMemo, useRef, type RefObject } from 'react'

import varReplacer from '../utils/varReplacer'
import { useTranslation } from '../contexts/localization/Localization'
import { processReplacements } from '../utils/processReplacements'
import { useAppContext } from '../contexts/AppContext'
import insertContentWithStyles from '../utils/insertHtmlContentWithStyles'
import { QuestionData } from '@playbooks/action-cards-utils/src'
import dictionary from '../contexts/localization/dictionary'
import { ActionCardsSettings } from '../types'

export default function useReplacements(
  containerWithReplacing: RefObject<HTMLElement | null>,
  initialString: string,
  deps?: unknown[],
) {
  const { settings, questionsWithCode } = useAppContext()
  const { text } = useTranslation()
  const tidRef = useRef<ReturnType<typeof setTimeout> | null>(null)

  const button = useMemo(() => {
    return (cb: (e: MouseEvent) => void) => {
      const button = document.createElement('button')

      button.type = 'button'
      button.innerHTML =
        '<svg xmlns="http://www.w3.org/2000/svg" height="1em" viewBox="0 0 448 512"><!--! Font Awesome Free 6.4.2 by @fontawesome - https://fontawesome.com License - https://fontawesome.com/license (Commercial License) Copyright 2023 Fonticons, Inc. --><path d="M208 0H332.1c12.7 0 24.9 5.1 33.9 14.1l67.9 67.9c9 9 14.1 21.2 14.1 33.9V336c0 26.5-21.5 48-48 48H208c-26.5 0-48-21.5-48-48V48c0-26.5 21.5-48 48-48zM48 128h80v64H64V448H256V416h64v48c0 26.5-21.5 48-48 48H48c-26.5 0-48-21.5-48-48V176c0-26.5 21.5-48 48-48z"/></svg>'
      button.classList.add('actionCard_CopyButton')
      button.addEventListener('click', cb)
      button.title = 'Copy'

      return button
    }
  }, [])

  useEffect(() => {
    applyReplacements(containerWithReplacing, initialString, settings, text, questionsWithCode, button)
  }, deps)

  useEffect(() => {
    clearTimeout(tidRef.current!)

    tidRef.current = setTimeout(
      () => applyReplacements(containerWithReplacing, initialString, settings, text, questionsWithCode, button),
      500,
    ) as unknown as ReturnType<typeof setTimeout>

    return () => {
      if (tidRef.current) {
        clearTimeout(tidRef.current)
      }
    }
  }, [JSON.stringify(questionsWithCode)])
}

function applyReplacements(
  containerWithReplacing: RefObject<HTMLElement | null>,
  initialString: string,
  settings: ActionCardsSettings,
  text: (key: keyof (typeof dictionary)['en' | 'de']) => string,
  questionsWithCode: QuestionData<unknown>[],
  button: (cb: (e: MouseEvent) => void) => HTMLButtonElement,
) {
  if (!containerWithReplacing.current) return
  const root = containerWithReplacing.current

  // If the root has shadow DOM, we need to apply replacements there
  if (!root.shadowRoot) {
    root.attachShadow({ mode: 'open' })
  }

  const shadowRoot = root.shadowRoot!

  let replacedString = initialString.replace(/{copy}\[\[(.*?)\]\]/g, '<span class="actionCard_CopyableText">$1</span>')

  replacedString = replacedString.replace(/{lib}\[\[(.*?)\]\]/g, (_1, uniqCode) => {
    return `<a target="_blank" href="${settings?.tenantUrl}/go/${uniqCode}?public=true&projectId=${settings?.playId}" class="actionCard_LibItem ${!settings?.tenantUrl ? 'inactive' : ''}">${text('openLibItem')}</a>`
  })

  if (settings?.additionalData) {
    replacedString = replacedString.replace(/{data}\[\[(.*?)\]\]/g, varReplacer(settings.additionalData))
    replacedString = replacedString.replace(/\$(\w+)/g, varReplacer(settings.additionalData))
  }

  const prevReplacedString = replacedString

  replacedString = processReplacements(replacedString, questionsWithCode, false) || prevReplacedString

  insertContentWithStyles(replacedString, shadowRoot).then(() => {
    const copyableElements = shadowRoot.querySelectorAll('.actionCard_CopyableText')

    copyableElements.forEach(el => {
      el.appendChild(
        button(async e => {
          const b = e.currentTarget as HTMLButtonElement

          await navigator.clipboard.writeText(el.textContent || '')

          b.classList.add('actionCard_text_is_copied')

          setTimeout(() => {
            b.classList.remove('actionCard_text_is_copied')
          }, 3000)
        }),
      )
    })
  })
}
